import { T, useGT } from 'gt-react';
import { ArrowDownIcon, ArrowUpIcon, XIcon } from '@phosphor-icons/react';
import type { TaskTypeResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { SingleSelect } from 'src/components/common/SingleSelect';
import { TaskPageDefaultColumnsField } from './TaskPageDefaultColumnsField';
import {
  MAX_TASK_TYPE_LABEL_LENGTH,
  addDraftTaskType,
  availableTaskTypeOptions,
  canAddDraftTaskType,
  moveDraftTaskType,
  removeDraftTaskType,
  updateDraftTaskType,
  type TaskPageTaskTypeDraft,
} from './taskPageAdmin';

/** One task type on the page: its tab label, order, and default columns. */
function TaskPageTaskTypeRow({
  entry,
  taskType,
  index,
  count,
  onMove,
  onUpdate,
  onRemove,
}: {
  entry: TaskPageTaskTypeDraft;
  taskType: TaskTypeResponse | undefined;
  index: number;
  count: number;
  onMove: (delta: -1 | 1) => void;
  onUpdate: (
    updates: Partial<Omit<TaskPageTaskTypeDraft, 'taskTypeId'>>,
  ) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const typeName = taskType?.name ?? entry.taskTypeId;
  return (
    <li className="flex flex-col gap-2 rounded border p-2">
      <div className="flex items-center gap-1.5">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {typeName}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label={gt('Move task type {name} up', {
            context: 'button in the task page task types editor',
            name: typeName,
          })}
        >
          <ArrowUpIcon className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          aria-label={gt('Move task type {name} down', {
            context: 'button in the task page task types editor',
            name: typeName,
          })}
        >
          <ArrowDownIcon className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-6"
          disabled={count === 1}
          onClick={onRemove}
          aria-label={gt('Remove task type {name}', {
            context: 'button in the task page task types editor',
            name: typeName,
          })}
        >
          <XIcon className="size-3" />
        </Button>
      </div>
      {count > 1 && (
        <Input
          value={entry.label}
          maxLength={MAX_TASK_TYPE_LABEL_LENGTH}
          onChange={(e) => onUpdate({ label: e.target.value })}
          className="h-7 text-xs"
          placeholder={typeName}
          aria-label={gt('Tab label for {name}', {
            context:
              'input in the task page task types editor; optional label for this task type tab',
            name: typeName,
          })}
        />
      )}
      <TaskPageDefaultColumnsField
        value={entry.defaultColumns}
        onChange={(defaultColumns) => onUpdate({ defaultColumns })}
        taskType={taskType}
      />
    </li>
  );
}

/**
 * The ordered task types a task page shows. One type renders a plain queue;
 * several render one tab each, in this order, with an optional tab label and
 * their own default columns (columns are type-specific).
 */
export function TaskPageTaskTypesField({
  id,
  value,
  onChange,
  taskTypes,
}: {
  id: string;
  value: TaskPageTaskTypeDraft[];
  onChange: (next: TaskPageTaskTypeDraft[]) => void;
  taskTypes: TaskTypeResponse[] | undefined;
}) {
  const gt = useGT();
  const typeById = new Map((taskTypes ?? []).map((t) => [t.id, t]));
  const addOptions = availableTaskTypeOptions(
    (taskTypes ?? []).map((t) => ({ id: t.id, label: t.name })),
    value,
  );

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        <T context="task page form field label">Task types</T>
      </Label>
      {value.length > 0 && (
        <ul className="flex flex-col gap-2">
          {value.map((entry, index) => (
            <TaskPageTaskTypeRow
              key={entry.taskTypeId}
              entry={entry}
              taskType={typeById.get(entry.taskTypeId)}
              index={index}
              count={value.length}
              onMove={(delta) =>
                onChange(moveDraftTaskType(value, index, delta))
              }
              onUpdate={(updates) =>
                onChange(updateDraftTaskType(value, index, updates))
              }
              onRemove={() => onChange(removeDraftTaskType(value, index))}
            />
          ))}
        </ul>
      )}
      <SingleSelect
        id={id}
        options={addOptions}
        value={null}
        onChange={(option) =>
          onChange(addDraftTaskType(value, option?.id ?? ''))
        }
        disabled={!canAddDraftTaskType(value) || addOptions.length === 0}
        placeholder={
          value.length === 0
            ? gt('Select a task type', {
                context: 'task type picker placeholder in the task page editor',
              })
            : gt('Add another task type', {
                context:
                  'picker placeholder in the task page editor, adds a task type shown as another tab',
              })
        }
        fullWidth
      />
      <p className="text-[11px] text-muted-foreground">
        <T context="hint under the task page task types field">
          The page shows the queue of tasks of these types. With more than one
          type, each gets its own tab, in this order.
        </T>
      </p>
    </div>
  );
}
